import React,{useEffect,useMemo,useRef,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PostArtwork from './PostArtwork';
import DownloadPostButton from './DownloadPostButton';
import {postLayouts} from '../data/postLayouts';
import * as api from '../lib/api.js';
import './ReviewCaption.css';

const {
  FiCheck,
  FiCopy,
  FiEdit2,
  FiFacebook,
  FiImage,
  FiInstagram,
  FiLayout,
  FiLinkedin,
  FiRefreshCw,
  FiType,
  FiUploadCloud,
  FiX,
  FiZap
}=FiIcons;

const platforms=[
  {id:'instagram',label:'Instagram',icon:FiInstagram},
  {id:'linkedin',label:'LinkedIn',icon:FiLinkedin},
  {id:'facebook',label:'Facebook',icon:FiFacebook}
];

function isPersistedPost(postId) {
  return typeof postId==='string' && postId.includes('-');
}

function getCaption(post,platform) {
  if (post.caption && typeof post.caption==='object') {
    return post.caption[platform] || post.caption.instagram || '';
  }

  return post.caption || '';
}

function getNextLayout(currentLayout,preferredLayouts=[]) {
  const preferred=preferredLayouts.filter(
    (layout)=> postLayouts.includes(layout) && layout!==currentLayout
  );
  const remaining=postLayouts.filter((layout)=> layout!==currentLayout);
  const biased=[...preferred,...preferred,...remaining];

  return biased[Math.floor(Math.random()*biased.length)] || 'editorial';
}

function TuningControl({icon,label,action,loading,onClick,children}) {
  return (
    <div className="review-tuning-control">
      <span className="tuning-icon">
        <SafeIcon icon={icon} />
      </span>
      <div>
        <small>{label}</small>
        <div className="tuning-actions">
          <button type="button" disabled={loading} onClick={onClick}>
            <SafeIcon
              icon={FiRefreshCw}
              className={loading ? 'regenerate-spinner' : ''}
            />
            {loading ? 'Regenerating...' : action}
          </button>
          {children}
        </div>
      </div>
    </div>
  );
}

function ImageTuningControl({
  regenerating,
  uploading,
  onRegenerate,
  onUpload
}) {
  const busy=regenerating || uploading;

  return (
    <TuningControl
      icon={FiImage}
      label="Image"
      action="Try another image"
      loading={busy}
      onClick={onRegenerate}
    >
      <span className="tuning-divider" aria-hidden="true" />
      <label
        className={`upload-own-control ${busy ? 'disabled' : ''}`}
        aria-disabled={busy}
      >
        <SafeIcon
          icon={FiUploadCloud}
          className={uploading ? 'regenerate-spinner' : ''}
        />
        {uploading ? 'Uploading...' : 'Upload your own'}
        <input
          type="file"
          accept="image/*"
          disabled={busy}
          onChange={onUpload}
        />
      </label>
    </TuningControl>
  );
}

function ReviewScreen({
  post,
  brand,
  accountId,
  brandId,
  onApprove,
  onReject,
  onAssetUploaded
}) {
  const [draft,setDraft]=useState(()=> ({...post}));
  const [seedIdea,setSeedIdea]=useState(()=> post.prompt || post.seedContent || '');
  const [rejecting,setRejecting]=useState(false);
  const [reason,setReason]=useState('');
  const [copied,setCopied]=useState(false);
  const [activePlatform,setActivePlatform]=useState('instagram');
  const [uploading,setUploading]=useState(false);
  const [enhancing,setEnhancing]=useState(false);
  const [actionLoading,setActionLoading]=useState(null);
  const [actionError,setActionError]=useState('');
  const [captionError,setCaptionError]=useState('');
  const [regenerating,setRegenerating]=useState({
    text:false,
    layout:false,
    image:false
  });
  const timers=useRef([]);
  const artworkRef=useRef(null);

  const persisted=isPersistedPost(post.id);
  const busy=Boolean(actionLoading) || uploading || enhancing ||
    Object.values(regenerating).some(Boolean);

  const clearTimers=()=> {
    timers.current.forEach((timer)=> window.clearTimeout(timer));
    timers.current=[];
  };

  useEffect(()=> {
    clearTimers();
    setDraft({...post});
    setSeedIdea(post.prompt || post.seedContent || '');
    setRejecting(false);
    setReason('');
    setCopied(false);
    setUploading(false);
    setEnhancing(false);
    setActionLoading(null);
    setActionError('');
    setCaptionError('');
    setRegenerating({text:false,layout:false,image:false});
  },[post]);

  useEffect(()=> {
    if (!persisted) {
      return undefined;
    }

    let cancelled=false;

    api.postCaptions.getCaptions(post.id).then(({data,error})=> {
      if (cancelled) {
        return;
      }

      if (error) {
        setCaptionError(error.message || 'Failed to load captions.');
        return;
      }

      if (data?.length) {
        setDraft((current)=> ({
          ...current,
          caption:Object.fromEntries(
            data.map((row)=> [row.platform,row.caption])
          )
        }));
      }
    });

    return ()=> {
      cancelled=true;
    };
  },[post.id,persisted]);

  useEffect(()=> {
    return ()=> {
      clearTimers();
    };
  },[]);

  const activeCaption=useMemo(
    ()=> getCaption(draft,activePlatform),
    [draft,activePlatform]
  );

  const persistDraft=async (nextDraft)=> {
    if (!persisted) {
      return {error:null};
    }

    const {error}=await api.posts.savePostDraft(nextDraft.id,nextDraft);

    if (error) {
      setActionError(error.message || 'Failed to save post changes.');
    }

    return {error};
  };

  const runRegeneration=(part,updateDraft)=> {
    if (regenerating[part]) {
      return;
    }

    setActionError('');
    setRegenerating((current)=> ({...current,[part]:true}));

    const timer=window.setTimeout(()=> {
      setDraft((current)=> {
        const next=updateDraft(current);

        if (persisted) {
          persistDraft(next);
        }

        return next;
      });
      setRegenerating((current)=> ({...current,[part]:false}));
    },650);

    timers.current.push(timer);
  };

  const draftWithIdea=(current)=> ({
    ...current,
    prompt:seedIdea.trim(),
    seedContent:seedIdea.trim()
  });

  const regenerateText=async ()=> {
    if (regenerating.text || busy) {
      return;
    }

    const trimmedIdea=seedIdea.trim();

    if (!trimmedIdea) {
      setActionError('Enter a post idea before regenerating.');
      return;
    }

    if (!persisted) {
      setActionError('Regeneration is only available for saved posts.');
      return;
    }

    if (!brandId) {
      setActionError('Missing brand context for regeneration.');
      return;
    }

    setActionError('');
    setRegenerating((current)=> ({...current,text:true}));

    try {
      const {error:promptError}=await api.posts.updatePost(draft.id,{
        prompt:trimmedIdea
      });

      if (promptError) {
        throw promptError;
      }

      const {data:generated,error:generateError}=await api.posts.generatePost({
        brandId,
        seedType:draft.inputMode || 'prompt',
        seedContent:trimmedIdea,
        postId:draft.id
      });

      if (generateError) {
        throw generateError;
      }

      const {error:updateError}=await api.posts.updatePost(draft.id,{
        headline:generated.headline,
        subhead:generated.subhead
      });

      if (updateError) {
        throw updateError;
      }

      for (const [platform,caption] of Object.entries(generated.captions || {})) {
        const {error:captionError}=await api.postCaptions.upsertCaption(
          draft.id,
          platform,
          caption
        );

        if (captionError) {
          throw captionError;
        }
      }

      setDraft((current)=> ({
        ...current,
        prompt:trimmedIdea,
        seedContent:trimmedIdea,
        headline:generated.headline,
        supporting:generated.subhead,
        caption:generated.captions || current.caption,
        image:api.posts.resolvePostImageUrl(generated.imageStoragePath),
        imageStoragePath:generated.imageStoragePath,
        imageCredit:generated.photographerCredit || null
      }));
    } catch (error) {
      setActionError(error?.message || 'Failed to regenerate post.');
    } finally {
      setRegenerating((current)=> ({...current,text:false}));
    }
  };

  const enhanceIdea=async ()=> {
    const trimmedIdea=seedIdea.trim();

    if (!trimmedIdea || enhancing || busy) {
      return;
    }

    setActionError('');
    setEnhancing(true);

    try {
      const {data,error}=await api.brands.enhancePostIdea({
        brandId,
        seedContent:trimmedIdea
      });

      if (error) {
        throw error;
      }

      setSeedIdea(data.enhancedIdea);
    } catch (error) {
      setActionError(error?.message || 'Failed to enhance post idea.');
    } finally {
      setEnhancing(false);
    }
  };

  const regenerateLayout=()=> {
    runRegeneration('layout',(current)=> ({
      ...current,
      layout:getNextLayout(current.layout,brand.preferredLayouts)
    }));
  };

  const regenerateImage=async ()=> {
    if (regenerating.image || busy) {
      return;
    }

    if (!persisted) {
      setActionError('Image regeneration is only available for saved posts.');
      return;
    }

    if (!brandId) {
      setActionError('Missing brand context for image regeneration.');
      return;
    }

    setActionError('');
    setRegenerating((current)=> ({...current,image:true}));

    try {
      const {data:generated,error:generateError}=await api.posts.generatePost({
        brandId,
        postId:draft.id,
        mode:'image_only'
      });

      if (generateError) {
        throw generateError;
      }

      setDraft((current)=> ({
        ...current,
        image:api.posts.resolvePostImageUrl(generated.imageStoragePath),
        imageStoragePath:generated.imageStoragePath,
        imageCredit:generated.photographerCredit || null
      }));
    } catch (error) {
      setActionError(error?.message || 'Failed to regenerate image.');
    } finally {
      setRegenerating((current)=> ({...current,image:false}));
    }
  };

  const uploadImage=async (event)=> {
    const file=event.target.files?.[0];
    event.target.value='';

    if (!file || !file.type.startsWith('image/') || uploading) {
      return;
    }

    if (!persisted) {
      setActionError('Upload is only available for saved posts.');
      return;
    }

    if (!accountId || !brandId) {
      setActionError('Missing account or brand context for upload.');
      return;
    }

    setActionError('');
    setUploading(true);

    const {data,error}=await api.posts.uploadPostImage(
      accountId,
      brandId,
      post.id,
      file,
      file.name
    );

    setUploading(false);

    if (error) {
      setActionError(error.message || 'Failed to upload image.');
      return;
    }

    setDraft((current)=> ({
      ...current,
      image:data.publicUrl,
      imageStoragePath:data.storagePath
    }));

    onAssetUploaded?.({
      id:data.asset.id,
      imageUrl:data.publicUrl,
      label:data.asset.label,
      addedDate:'Just now',
      source:'post-upload',
      storagePath:data.storagePath
    });
  };

  const copyCaption=async ()=> {
    await navigator.clipboard?.writeText(activeCaption);
    setCopied(true);
    window.setTimeout(()=> setCopied(false),1400);
  };

  const changePlatform=(platform)=> {
    setActivePlatform(platform);
    setCopied(false);
  };

  const handleApprove=async ()=> {
    setActionError('');

    if (busy) {
      return;
    }

    setActionLoading('approve');

    try {
      const nextDraft=draftWithIdea(draft);

      if (persisted) {
        const {error:saveError}=await api.posts.savePostDraft(draft.id,nextDraft);

        if (saveError) {
          throw saveError;
        }

        const {data,error}=await api.posts.approvePost(draft.id);

        if (error) {
          throw error;
        }

        onApprove(api.posts.mapDbPostToLocal(data,nextDraft.caption));
        return;
      }

      onApprove({...nextDraft,status:'approved'});
    } catch (error) {
      setActionError(error?.message || 'Failed to approve post.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject=async ()=> {
    const trimmedReason=reason.trim();

    if (!trimmedReason || busy) {
      return;
    }

    setActionError('');
    setActionLoading('reject');

    try {
      const nextDraft=draftWithIdea(draft);

      if (persisted) {
        const {error:saveError}=await api.posts.savePostDraft(draft.id,nextDraft);

        if (saveError) {
          throw saveError;
        }

        const {data,error}=await api.posts.rejectPost(draft.id,trimmedReason);

        if (error) {
          throw error;
        }

        onReject(
          trimmedReason,
          api.posts.mapDbPostToLocal(data,nextDraft.caption)
        );
        return;
      }

      onReject(trimmedReason,{
        ...nextDraft,
        status:'rejected',
        rejectionReason:trimmedReason
      });
    } catch (error) {
      setActionError(error?.message || 'Failed to reject post.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <motion.main
      className="page review-page"
      initial={{opacity:0}}
      animate={{opacity:1}}
    >
      <header className="review-header">
        <div>
          <span className="eyebrow">Ready for review</span>
          <h1>Your post is ready.</h1>
        </div>
        <div className="review-actions">
          <DownloadPostButton
            artworkRef={artworkRef}
            headline={draft.headline}
            disabled={busy}
          />
          <button
            className="secondary-button"
            disabled={busy}
            onClick={()=> setRejecting(true)}
          >
            <SafeIcon icon={FiX} />
            Reject
          </button>
          <button
            className="primary-button"
            style={{background:brand.primary}}
            disabled={busy}
            onClick={handleApprove}
          >
            <SafeIcon icon={FiCheck} />
            {actionLoading==='approve' ? 'Approving...' : 'Approve'}
          </button>
        </div>
      </header>

      {(actionError || captionError) && (
        <p className="review-error" role="alert">
          {actionError || captionError}
        </p>
      )}

      <div className="review-layout">
        <div className="review-preview-column">
          <motion.div
            className="preview-frame"
            initial={{y:16}}
            animate={{y:0}}
          >
            <PostArtwork ref={artworkRef} post={draft} brand={brand} />
          </motion.div>

          <div className="review-idea-editor">
            <label htmlFor="review-seed-idea">
              <SafeIcon icon={FiEdit2} />
              <span>Post idea</span>
            </label>
            <textarea
              id="review-seed-idea"
              className="review-idea-input"
              value={seedIdea}
              disabled={busy}
              onChange={(event)=> setSeedIdea(event.target.value)}
              placeholder="Edit the core idea before regenerating"
              rows={3}
            />
            <button
              type="button"
              className="review-enhance-button"
              disabled={busy || !seedIdea.trim()}
              onClick={enhanceIdea}
            >
              <SafeIcon
                icon={FiZap}
                className={enhancing ? 'regenerate-spinner' : ''}
              />
              {enhancing ? 'Enhancing...' : 'Enhance with AI'}
            </button>
          </div>

          <div className="review-tuning-bar" aria-label="Regenerate post parts">
            <TuningControl
              icon={FiType}
              label="Text"
              action="Try another version"
              loading={regenerating.text}
              onClick={regenerateText}
            />
            <TuningControl
              icon={FiLayout}
              label="Layout"
              action="Try another layout"
              loading={regenerating.layout}
              onClick={regenerateLayout}
            />
            <ImageTuningControl
              regenerating={regenerating.image}
              uploading={uploading}
              onRegenerate={regenerateImage}
              onUpload={uploadImage}
            />
          </div>
        </div>

        <aside className="caption-panel">
          <div className="panel-title">
            <div>
              <span>Post copy</span>
              <h3>Caption</h3>
            </div>
            <button onClick={copyCaption}>
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <div
            className="platform-tabs"
            role="tablist"
            aria-label="Caption platform"
          >
            {platforms.map((platform)=> (
              <button
                key={platform.id}
                className={activePlatform===platform.id ? 'active' : ''}
                role="tab"
                aria-selected={activePlatform===platform.id}
                onClick={()=> changePlatform(platform.id)}
              >
                <SafeIcon icon={platform.icon} />
                {platform.label}
              </button>
            ))}
          </div>

          <p className="platform-caption">{activeCaption}</p>
        </aside>
      </div>

      {rejecting && (
        <div
          className="modal-backdrop"
          onMouseDown={()=> !busy && setRejecting(false)}
        >
          <motion.div
            className="reject-modal"
            onMouseDown={(event)=> event.stopPropagation()}
            initial={{opacity:0,scale:0.96}}
            animate={{opacity:1,scale:1}}
          >
            <span className="eyebrow">Creative feedback</span>
            <h2>What should change?</h2>
            <p>Your note will be saved with this version.</p>
            {actionError && (
              <p className="review-error" role="alert">{actionError}</p>
            )}
            <textarea
              value={reason}
              onChange={(event)=> setReason(event.target.value)}
              placeholder="e.g. Make the headline feel more optimistic"
              autoFocus
            />
            <div className="modal-actions">
              <button
                className="text-button"
                disabled={busy}
                onClick={()=> setRejecting(false)}
              >
                Cancel
              </button>
              <button
                className="primary-button danger"
                disabled={!reason.trim() || busy}
                onClick={handleReject}
              >
                {actionLoading==='reject' ? 'Rejecting...' : 'Confirm rejection'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.main>
  );
}

export default ReviewScreen;
